"use client";
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { PopupButton, useCalendlyEventListener } from "react-calendly";
import cn from "classnames";

import useWindowDimensions from "@/next-src/hooks/useWindowDimensions";
import Button from "@/next-src/components/ui/button/Button";

import comCSS from "./Calendly.module.scss";
import convertHTMLtoJSX from "@/next-src/utils/convertHTMLtoJSX";

const Calendly = ({
  data,
  isPulsingIcon = false,
  className = "",
  classNameBtn = "",
  classNameBtnSuccess = "",
}) => {
  const { width: windowWidth } = useWindowDimensions();
  const [calendySuccess, setCallendySuccess] = useState(false);
  // useCalendlyEventListener is root and works for all Calendly components on the page.
  // If a customer schedules the event in one Calendly, the event `onEventScheduled` is fired in every Calendly component on the page.
  // btnIdRef is just the way to handle success in an active Calendly, and avoid all others.
  const btnIdRef = useRef(null);
  useCalendlyEventListener({
    onEventScheduled: (e) =>
      btnIdRef.current === data?.calendlyButton?.id &&
      setCallendySuccess(e.data.payload),
  });
  const [cssHover, setCssHover] = useState("");
  const { calendlyButtonMobile, calendlyButtonSuccessMobile } = data;
  let { calendlyButton, calendlyButtonSuccess } = data;

  calendlyButton = windowWidth >= 992 ? calendlyButton : calendlyButtonMobile;
  calendlyButtonSuccess =
    windowWidth >= 992 ? calendlyButtonSuccess : calendlyButtonSuccessMobile;

  const {
    bgColorHover,
    borderColorHover,
    id,
    bgColor,
    borderColor,
    calendlyLink,
    text,
    hide,
  } = calendlyButton || {};

  useEffect(() => {
    if (bgColorHover || borderColorHover) {
      setCssHover(`.button.btn-${id}-hover:hover {
             border-color: ${borderColorHover || ""} !important;
             background-color: ${bgColorHover || ""} !important;
           }
       `);
    }
  }, [bgColorHover, borderColorHover, setCssHover, id]);

  if (!calendlyLink || hide) return null;

  if (calendySuccess) {
    return (
      <div className={cn(comCSS.btnWrapperCalendly)}>
        <Button
          data={calendlyButtonSuccess}
          className={cn(classNameBtnSuccess, comCSS.btn)}
          type="button"
        />
      </div>
    );
  }

  const textEl = (
    <>
      {isPulsingIcon ? (
        <div className={cn(comCSS.btnMark, "pulsing-btn-dot-div")} />
      ) : null}
      <div>{convertHTMLtoJSX(text)}</div>
    </>
  );

  return (
    <div
      className={cn(comCSS.btnWrapperCalendly, className)}
      onClick={(e) => {
        btnIdRef.current = data?.calendlyButton?.id;
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <style>{cssHover}</style>
      <PopupButton
        styles={{
          backgroundColor: bgColor,
          borderColor: borderColor,
        }}
        url={calendlyLink}
        /*
         * react-calendly uses React's Portal feature (https://reactjs.org/docs/portals.html) to render the popup modal. As a result, you'll need to
         * specify the rootElement property to ensure that the modal is inserted into the correct domNode.
         */
        rootElement={
          typeof window !== "undefined"
            ? document.getElementById("calendlyWrapper")
            : null
        }
        text={textEl}
        className={cn(comCSS.btn, classNameBtn, "button w-inline-block", {
          [`btn-${id}-hover`]: bgColorHover || borderColorHover,
        })}
        // prefill={{
        //   name: values.name,
        //   email: values.email
        // }}
      />
    </div>
  );
};

Calendly.propTypes = {
  className: PropTypes.string,
  classNameBtn: PropTypes.string,
  classNameBtnSuccess: PropTypes.string,
  data: PropTypes.shape({
    calendlyButton: PropTypes.shape({
      id: PropTypes.string.isRequired,
      text: PropTypes.node, // Allows for various button text content (e.g., strings or JSX)
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
      calendlyLink: PropTypes.string,
    }),
    calendlyButtonMobile: PropTypes.shape({
      id: PropTypes.string.isRequired,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
      calendlyLink: PropTypes.string,
    }),
    calendlyButtonSuccess: PropTypes.shape({
      id: PropTypes.string.isRequired,
      text: PropTypes.node, // Allows for various button text content (e.g., strings or JSX)
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
    calendlyButtonSuccessMobile: PropTypes.shape({
      id: PropTypes.string.isRequired,
      text: PropTypes.node, // Allows for various button text content (e.g., strings or JSX)
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
  }).isRequired,
  isPulsingIcon: PropTypes.bool,
};

export default Calendly;
